import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import styled, { css } from 'styled-components';
import {
  useAskEconChatMutation,
  useListEconChatMessagesQuery,
  useListEconChatSessionsQuery,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Loader } from '@shared/ui/Progress';
import { riseIn } from '@shared/theme/animations';
import { useEconPermission } from '@entities/economic/types';

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 520px;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
`;

const Header = styled.div`
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const HeaderTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const Hint = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Stream = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Bubble = styled.div<{ $role: 'user' | 'assistant' }>`
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  max-width: 80%;
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.lg};
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-wrap: break-word;
  animation: ${riseIn} 240ms ${({ theme }) => theme.motion.easeOut};

  ${({ $role, theme }) =>
    $role === 'user'
      ? css`
          background: ${theme.colors.primary};
          color: #fff;
          border-bottom-right-radius: ${theme.radius.sm};
        `
      : css`
          background: ${theme.colors.surface};
          color: ${theme.colors.text.primary};
          border: 1px solid ${theme.colors.border};
          border-bottom-left-radius: ${theme.radius.sm};
        `}
`;

const Empty = styled.div`
  margin: auto;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 13px;
  max-width: 360px;
  line-height: 1.55;
`;

const TypingRow = styled.div`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ErrBubble = styled.div`
  align-self: flex-start;
  max-width: 80%;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.status.danger}12;
  border: 1px solid ${({ theme }) => theme.colors.status.danger}33;
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

const Composer = styled.form`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(3)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const TextArea = styled.textarea`
  flex: 1;
  resize: none;
  min-height: 40px;
  max-height: 120px;
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3.5)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 14px;
  line-height: 1.4;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    box-shadow ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

/** Максимум символов вопроса — зеркалит backend MAX_QUESTION_CHARS. */
const MAX_QUESTION = 4000;

/**
 * Чат-расследование по обработке. Read-only диалог: агент объясняет уже
 * посчитанные факты/аномалии обработки, ничего не пересчитывая. Один активный
 * тред на обработку — берём последнюю сессию (бэкенд отдаёт новые сверху), её
 * сообщения и дописываем в неё. Первый вопрос создаёт сессию на backend; её id
 * приходит в ответе `askEconChat` и подхватывается через инвалидацию.
 */
export function RunChatPanel({ runId }: { runId: string }) {
  const { can_write } = useEconPermission();
  const sessionsQ = useListEconChatSessionsQuery(runId);
  const [ask, { isLoading: asking }] = useAskEconChatMutation();

  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Оптимистичная реплика пользователя, пока ждём ответ ассистента.
  const [pending, setPending] = useState<string | null>(null);
  const streamRef = useRef<HTMLDivElement>(null);

  // Активная сессия — самая свежая (бэкенд сортирует новые сверху).
  const activeSessionId = useMemo(() => sessionsQ.data?.items[0]?.id ?? null, [sessionsQ.data]);

  const messagesQ = useListEconChatMessagesQuery(activeSessionId ?? '', {
    skip: !activeSessionId,
  });

  const messages = useMemo(() => messagesQ.data?.items ?? [], [messagesQ.data]);

  // Автоскролл к последнему сообщению.
  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [messages.length, pending, asking]);

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    const question = text.trim();
    if (!question || asking || !can_write) return;
    setError(null);
    setText('');
    setPending(question);
    try {
      await ask({
        runId,
        question,
        session_id: activeSessionId ?? undefined,
      }).unwrap();
      // Сессии/сообщения перечитаются по инвалидации тегов.
    } catch (err) {
      setError('Не удалось получить ответ агента. Попробуйте переформулировать вопрос.');
      // eslint-disable-next-line no-console
      console.error('askEconChat failed', err);
    } finally {
      setPending(null);
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  }

  const isEmpty = messages.length === 0 && !pending && !asking;

  return (
    <Panel>
      <Header>
        <HeaderTitle>Расследование по обработке</HeaderTitle>
        <Hint>Enter — отправить · Shift+Enter — новая строка</Hint>
      </Header>
      <Stream ref={streamRef}>
        {isEmpty && (
          <Empty>
            Спросите агента по этой обработке — например, «почему по объекту маржа ниже плана?» или
            «откуда взялась эта цифра?». Агент объясняет уже посчитанные факты и аномалии и не
            пересчитывает их.
          </Empty>
        )}
        {messages.map((m) => (
          <Bubble key={m.id} $role={m.role}>
            {m.content}
          </Bubble>
        ))}
        {pending && <Bubble $role="user">{pending}</Bubble>}
        {asking && (
          <TypingRow>
            <Loader /> Агент анализирует обработку…
          </TypingRow>
        )}
        {error && <ErrBubble>{error}</ErrBubble>}
      </Stream>
      <Composer onSubmit={submit}>
        <TextArea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          placeholder={can_write ? 'Спросите агента по обработке…' : 'Только просмотр'}
          maxLength={MAX_QUESTION}
          rows={1}
          disabled={!can_write}
          aria-label="Вопрос агенту по обработке"
        />
        <Button
          type="submit"
          $variant="primary"
          disabled={asking || !text.trim() || !can_write}
          title={!can_write ? 'Только просмотр' : undefined}
        >
          {asking ? 'Жду…' : 'Спросить'}
        </Button>
      </Composer>
    </Panel>
  );
}
